import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const DrawerHeader = ({ onClose, title, subtitle, t }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      padding: "18px 20px 14px",
      borderBottom: `1px solid ${t.border2}`,
      flexShrink: 0,
    }}
  >
    <Box>
      <Typography
        sx={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "15px",
          fontWeight: 700,
          color: t.text,
          letterSpacing: "-0.01em",
        }}
      >
        {title || "New Position ID"}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "9px",
          color: t.text3,
          letterSpacing: "0.08em",
          mt: "3px",
        }}
      >
        {subtitle || "PMO · Psi360"}
      </Typography>
    </Box>
    <Box
      onClick={onClose}
      sx={{
        width: "28px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "3px",
        cursor: "pointer",
        color: t.text3,
        fontSize: "14px",
        transition: "all 0.15s",
        "&:hover": { background: t.surface2, color: t.text },
      }}
    >
      ✕
    </Box>
  </Box>
);

const DrawerFooter = ({ onClose, onSave, saving, isEdit, isDark, t }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      gap: "8px",
      padding: "14px 20px",
      borderTop: `1px solid ${t.border2}`,
      flexShrink: 0,
    }}
  >
    <Box
      onClick={onClose}
      sx={{
        padding: "8px 20px",
        borderRadius: "3px",
        cursor: "pointer",
        border: `1px solid ${t.border2}`,
        fontFamily: "'DM Mono', monospace",
        fontSize: "11px",
        color: t.text2,
        transition: "all 0.15s",
        "&:hover": { borderColor: t.border3, color: t.text },
      }}
    >
      Cancel
    </Box>
    <Box
      onClick={saving ? undefined : onSave}
      sx={{
        padding: "8px 24px",
        borderRadius: "3px",
        cursor: saving ? "not-allowed" : "pointer",
        background: saving ? t.surface3 : t.accent,
        fontFamily: "'DM Mono', monospace",
        fontSize: "11px",
        fontWeight: 600,
        color: saving ? t.text3 : isDark ? "#060E0D" : "#fff",
        transition: "all 0.15s",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        "&:hover": saving ? {} : { opacity: 0.9 },
      }}
    >
      {saving && <CircularProgress size={10} sx={{ color: "inherit" }} />}
      {saving ? "Saving..." : isEdit ? "Save Changes" : "Save Position"}
    </Box>
  </Box>
);

const ApiErrorBox = ({ message }) => (
  <Box
    sx={{
      mt: "12px",
      padding: "10px 12px",
      background: "rgba(200,64,64,0.08)",
      border: "1px solid rgba(200,64,64,0.3)",
      borderRadius: "3px",
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "11px",
        color: "#c84040",
      }}
    >
      {message}
    </Typography>
  </Box>
);

const DrawerShell = ({
  onClose,
  onSave,
  saving,
  apiError,
  title,
  subtitle,
  isEdit,
  readOnly,
  children,
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  return (
    <>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          top: "52px",
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 199,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          top: "52px",
          right: 0,
          bottom: 0,
          width: "400px",
          zIndex: 200,
          background: t.surface,
          borderLeft: `1px solid ${t.border2}`,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.2)",
          overflow: "hidden",
        }}
      >
        <DrawerHeader
          onClose={onClose}
          title={title}
          subtitle={subtitle}
          t={t}
        />

        <Box sx={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {children}
          {apiError && <ApiErrorBox message={apiError} />}
        </Box>

        {/* Only render footer when NOT read-only */}
        {!readOnly && (
          <DrawerFooter
            onClose={onClose}
            onSave={onSave}
            saving={saving}
            isEdit={isEdit}
            isDark={isDark}
            t={t}
          />
        )}
      </Box>
    </>
  );
};

export default DrawerShell;
